import React, { useEffect, useState, useContext } from 'react';
import { useParams } from 'react-router-dom';
import BrowseLayout from '../components/home/BrowseLayout';
import ListingsGrid from '../components/home/ListingsGrid';
import { LoadingContext } from '../App';
import { sortListingsByRarity } from '../utils/sortListings';
import './SetListingsPage.css';

const SetListingsPage = () => {
    const { setName: slug } = useParams();
    const [originalSetName, setOriginalSetName] = useState('');
    const [setDetails, setSetDetails] = useState(null);
    const [listings, setListings] = useState([]);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { setLoading: setGlobalLoading } = useContext(LoadingContext);
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

    useEffect(() => {
        const fetchSetDetails = async () => {
            setGlobalLoading(true);
            try {
                const response = await fetch(`${apiBaseUrl}/sets`);
                if (!response.ok) throw new Error(`Network error: ${response.statusText}`);
                const sets = await response.json();
                const set = sets.find((s) => {
                    const setSlug = s.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
                    return setSlug === slug;
                });
                if (set) {
                    setOriginalSetName(set.name);
                    setSetDetails(set);
                } else {
                    setError('Set not found');
                }
            } catch (err) {
                setError(err.message);
            } finally {
                setGlobalLoading(false);
            }
        };
        fetchSetDetails();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slug]);

    useEffect(() => {
        const fetchListings = async () => {
            if (!originalSetName) return;
            setLoading(true);
            try {
                const response = await fetch(`${apiBaseUrl}/listings?set=${encodeURIComponent(originalSetName)}&page=${page}`);
                if (!response.ok) throw new Error(`Network error: ${response.statusText}`);
                const data = await response.json();
                setListings(sortListingsByRarity(data.listings || []));
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchListings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [originalSetName, page]);

    return (
        <BrowseLayout>
            {setDetails && (
                <div className="gh-sethead">
                    {setDetails.logo_image && (
                        <img className="gh-sethead-logo" src={setDetails.logo_image} alt={setDetails.name} />
                    )}
                    <div className="gh-sethead-info">
                        <h1 className="gh-sethead-name">{setDetails.name}</h1>
                        <p className="gh-sethead-series">{setDetails.series}</p>
                        <div className="gh-sethead-meta">
                            {setDetails.release_date && (
                                <span>Released {new Date(setDetails.release_date).toLocaleDateString()}</span>
                            )}
                            {setDetails.total != null && <span>{setDetails.total} cards</span>}
                        </div>
                    </div>
                    {setDetails.symbol_image && (
                        <img className="gh-sethead-symbol" src={setDetails.symbol_image} alt={`${setDetails.name} symbol`} />
                    )}
                </div>
            )}

            <ListingsGrid
                title="Listings"
                subtitle={error ? null : `${listings.length} on this page`}
                listings={listings}
                loading={loading}
                error={error}
            />

            {(listings.length > 0 || page > 1) && (
                <div className="gh-setpager">
                    <button
                        type="button"
                        className="gh-setpager-btn"
                        onClick={() => setPage((p) => Math.max(p - 1, 1))}
                        disabled={page === 1}
                    >
                        Prev
                    </button>
                    <span className="gh-setpager-label">Page {page}</span>
                    <button
                        type="button"
                        className="gh-setpager-btn"
                        onClick={() => setPage((p) => p + 1)}
                        disabled={listings.length === 0}
                    >
                        Next
                    </button>
                </div>
            )}
        </BrowseLayout>
    );
};

export default SetListingsPage;
